<template>
  <div class="measureMoreFilters">
    <section class="measureMoreFilters-titleBar">
      <div class="measureMoreFilters-titleBar-title">
        <h3>All Filters</h3>
      </div>

      <div class="measureMoreFilters-titleBar-controls">
        <div class="measureMoreFilters-titleBar-controls-clear">
          <neb-button type="text"
            @click="onClearClick"
          >Clear</neb-button>
          <div class="measureMoreFilters-titleBar-controls-count"
            v-show="filterCount > 0"
          >
            <span>{{filterCount}}</span>
          </div>
        </div>
        <div class="measureMoreFilters-titleBar-controls-buttons">
          <neb-button type="secondary"
            @click="$emit('cancel')"
          >
            Cancel
          </neb-button>

          <neb-button @click="onApplyClick">
            Apply
          </neb-button>
        </div>
      </div>
    </section>

    <section class="measureMoreFilters-content">
      <div class="measureMoreFilters-content-toggles">
        <neb-form-control label="Source">
          <neb-toggle-box v-model="formData.assetSources"
            :options="assetSourceOptions"
            id="assetSources"
            name="assetSources"
          />
        </neb-form-control>
      </div>

      <div class="measureMoreFilters-content-filterSection">
        <div class="measureMoreFilters-content-filterSection-title">
          <h3>Distribution Filters</h3>
        </div>
        <div class="measureMoreFilters-content-filterSection-filters">
          <neb-form-control label="Brand"
            width="260px"
          >
            <neb-multi-select id="measureMoreFilters-brand"
              name="measureMoreFilters-brand"
              placeholder="Brand"
              prefix="Brand"
              :options="brandOptions"
              v-model="formData.brandIds"
              highlightDirty
              :defaultValue="[ `${brandId}` ]"
              clearable
              @clear="brandIds = [ `${brandId}` ]"
            ></neb-multi-select>
          </neb-form-control>

          <neb-form-control label="Distribution"
            width="260px"
          >
            <neb-select id="measureMoreFilters-distribution"
              name="measureMoreFilters-distribution"
              prefix="Distribution"
              v-model="formData.distribution"
              :options="distributionOptions"
              highlightDirty
              defaultValue="all"
              clearable
              @clear="formData.distribution = 'all'"
            ></neb-select>
          </neb-form-control>
        </div>
      </div>

      <div class="measureMoreFilters-content-filterSection">
        <div class="measureMoreFilters-content-filterSection-title">
          <h3>Date Filters</h3>
        </div>
        <div class="measureMoreFilters-content-filterSection-filters">
          <neb-form-control label="Metric Date Range"
            width="260px"
          >
            <neb-date-picker :value="metricDatePickerValue"
              mode="range"
              prefix="Metric Date Range"
              :defaultOptions="datePickerOptions"
              highlightDirty
              :defaultValue="[ '-10000', '0' ]"
              clearable
              :disableDayFunc="disableDayFunc"
              @input="onMetricDaysInput"
              @clear="onMetricDaysInput([ '-10000', '0' ])"
            ></neb-date-picker>
          </neb-form-control>

          <neb-form-control label="Date Created"
            width="260px"
          >
            <neb-date-picker :value="createdDatePickerValue"
              mode="range"
              prefix="Date created"
              :defaultOptions="datePickerOptions"
              highlightDirty
              :defaultValue="[ '-10000', '0' ]"
              clearable
              :disableDayFunc="disableDayFunc"
              @input="onCreatedDaysInput"
              @clear="onCreatedDaysInput([ '-10000', '0' ])"
            ></neb-date-picker>
          </neb-form-control>
        </div>
      </div>

      <div class="measureMoreFilters-content-filterSection">
        <div class="measureMoreFilters-content-filterSection-title">
          <h3>Organizational Filters</h3>
        </div>
        <div class="measureMoreFilters-content-filterSection-filters">
          <neb-form-control label="Properties/Values"
            width="260px"
          >
            <neb-multi-searchable-select id="measureMoreFilters-customProperties"
              name="measureMoreFilters-customProperties"
              :value="formData.customProperties"
              @input="value => formData.customProperties = value"
              :options="propertyOptions"
              placeholder="Select property"
              :divider="true"
              fitToContainer
            />
          </neb-form-control>

          <neb-form-control label="Topics"
            width="260px"
          >
            <neb-multi-searchable-nested-select id="measureMoreFilters-topics"
              name="measureMoreFilters-topics"
              :value="formData.topics"
              @input="value => formData.topics = value"
              :options="topicOptions"
              placeholder="Select topic"
              fitToContainer
            />
          </neb-form-control>

          <neb-form-control label="Collection"
            width="260px"
          >
            <neb-select id="measureMoreFilters-collection"
              name="measureMoreFilters-collection"
              v-model="formData.collection"
              :options="collectionOptions"
              placeholder="Select collection"
              highlightDirty
              :defaultValue="''"
              clearable
              emptyText="No Collections"
              @clear="formData.collection = ''"
            ></neb-select>
          </neb-form-control>
        </div>
      </div>

      <div class="measureMoreFilters-content-filterSection">
        <div class="measureMoreFilters-content-filterSection-title">
          <h3>Content Filters</h3>
        </div>
        <div class="measureMoreFilters-content-filterSection-filters">
          <neb-form-control label="Word Count"
            width="260px"
          >
            <neb-simple-range-select v-model="formData.wordCount"
              placeholder="All"
              prefix="Word Count"
              highlightDirty
              clearable
              :defaultValue="[]"
              :loading="filterMetricsLoading.wordCount"
              showMax
              showAvg
              :max="filterMetrics.wordCount.MAX__WORD_COUNT"
              :avg="filterMetrics.wordCount.AVG__WORD_COUNT"
              valueType="int"
              @action:openDropdown="onOpenRangeDropdown('wordCount')"
            />
          </neb-form-control>

          <neb-form-control label="Time To Consume"
            width="260px"
          >
            <neb-simple-range-select v-model="formData.timeToConsume"
              placeholder="All"
              prefix="Time to Consume"
              highlightDirty
              clearable
              :defaultValue="[]"
              :loading="filterMetricsLoading.duration"
              showMax
              showAvg
              :max="filterMetrics.duration.MAX__DURATION"
              :avg="filterMetrics.duration.AVG__DURATION"
              valueType="seconds"
              @action:openDropdown="onOpenRangeDropdown('duration')"
            />
          </neb-form-control>

          <neb-form-control label="Language"
            width="260px"
          >
            <neb-select id="measureMoreFilters-language"
              name="measureMoreFilters-language"
              v-model="formData.language"
              :options="languageOptions"
              placeholder="Select language"
              prefix="Language"
              highlightDirty
              :defaultValue="''"
              clearable
              @clear="formData.language = ''"
            ></neb-select>
          </neb-form-control>

          <neb-form-control label="Domain"
            width="260px"
          >
            <neb-select id="measureMoreFilters-domain"
              name="measureMoreFilters-domain"
              v-model="formData.domain"
              :options="domainOptions"
              placeholder="Select domain"
              highlightDirty
              :defaultValue="''"
              clearable
              searchable
              @clear="formData.domain = ''"
            ></neb-select>
          </neb-form-control>
        </div>
      </div>

      <div class="measureMoreFilters-content-filterSection">
        <div class="measureMoreFilters-content-filterSection-title">
          <h3>Metric Filters</h3>
        </div>
        <div class="measureMoreFilters-content-filterSection-filters">
          <neb-form-control label="Views"
            width="260px"
          >
            <neb-simple-range-select v-model="formData.views"
              placeholder="All"
              prefix="Views"
              highlightDirty
              clearable
              :defaultValue="[]"
              :loading="filterMetricsLoading.pageViews"
              showMax
              showAvg
              :max="filterMetrics.pageViews.MAX__PAGE_VIEWS"
              :avg="filterMetrics.pageViews.AVG__PAGE_VIEWS"
              valueType="int"
              @action:openDropdown="onOpenRangeDropdown('pageViews')"
            />
          </neb-form-control>

          <neb-form-control label="Social Actions"
            width="260px"
          >
            <neb-simple-range-select v-model="formData.socialActions"
                placeholder="All"
                prefix="Social Actions"
                highlightDirty
                clearable
                :defaultValue="[]"
                :loading="filterMetricsLoading.socialActions"
                showMax
                showAvg
                :max="filterMetrics.socialActions.MAX__SOCIAL_ACTIONS"
                :avg="filterMetrics.socialActions.AVG__SOCIAL_ACTIONS"
                valueType="int"
                @action:openDropdown="onOpenRangeDropdown('socialActions')"
            />
          </neb-form-control>

          <neb-form-control label="Completion Rate"
            width="260px"
          >
            <neb-simple-range-select v-model="formData.completionRate"
                placeholder="All"
                prefix="Completion Rate"
                highlightDirty
                clearable
                :defaultValue="[]"
                :loading="filterMetricsLoading.completionRate"
                showMax
                showAvg
                :max="filterMetrics.completionRate.MAX__COMPLETION_RATE"
                :avg="filterMetrics.completionRate.AVG__COMPLETION_RATE"
                valueType="percent"
                @action:openDropdown="onOpenRangeDropdown('completionRate')"
            />
          </neb-form-control>

          <neb-form-control label="Conversion Count"
            width="260px"
          >
            <neb-simple-range-select v-model="formData.conversionCount.value"
                placeholer="All"
                prefix="Conversions"
                highlightDirty
                clearable
                :defaultValue="[]"
                :loading="filterMetricsLoading.conversions"
                showMax
                showAvg
                :max="filterMetrics.conversions.MAX__CONVERSIONS"
                :avg="filterMetrics.conversions.AVG__CONVERSIONS"
                valueType="int"
                @action:openDropdown="onOpenRangeDropdown('conversions')"
            />
          </neb-form-control>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { includes, isEmpty } from 'lodash'

import NebToggleBox from '@/components/Assets/NebToggleBox'

const defaultFormData = () => ({
  assetSources: [ 'diffbot', 'nativo_advertiser' ],
  brandIds: [],
  distribution: 'all',
  metricDateStart: '-10000',
  metricDateEnd: '0',
  createdDateStart: '-10000',
  createdDateEnd: '0',
  customProperties: [],
  topics: [],
  collection: '',
  wordCount: [],
  timeToConsume: [],
  language: '',
  domain: '',
  views: [],
  socialActions: [],
  completionRate: [],
  conversionCount: {
    value: []
  }
})

export default {
  name: 'MeasureMoreFilters',

  components: {
    NebToggleBox
  },

  props: {
    filters: {
      type: Object,
      default: () => ({})
    },
    assetTypeOptions: {
      type: Array,
      default: () => []
    },
    assetSourceOptions: {
      type: Array,
      default: () => []
    },
    brandOptions: {
      type: Array,
      default: () => []
    },
    datePickerOptions: {
      type: Array,
      default: () => []
    },
    propertyOptions: {
      type: Array,
      default: () => []
    },
    topicOptions: {
      type: Array,
      default: () => []
    },
    collectionOptions: {
      type: Array,
      default: () => []
    },
    languageOptions: {
      type: Array,
      default: () => []
    },
    domainOptions: {
      type: Array,
      default: () => []
    },
    visible: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    formData: defaultFormData()
  }),

  computed: {
    filterMetricsLoading () { return this.$store.state.measure.filterMetricsLoading },
    filterMetrics () { return this.$store.state.measure.filterMetrics },
    filterCount () {
      return this.$store.getters['measure/selectedFilterCount']
    },
    metricDatePickerValue () {
      const {
        metricDateStart,
        metricDateEnd
      } = this.formData

      return [ metricDateStart, metricDateEnd ]
    },
    createdDatePickerValue () {
      const {
        createdDateStart,
        createdDateEnd
      } = this.formData

      return [ createdDateStart, createdDateEnd ]
    },
    distributionOptions () {
      return [
        { label: 'All', value: 'all' },
        { label: 'Sponsored', value: 'sponsored', disabled: !this.sponsoredContentEnabled },
        { label: 'Owned', value: 'owned' },
        { label: 'Earned', value: 'earned' }
      ]
    },
    sponsoredContentEnabled () {
      if (this.formData.brandIds.length === 1 && this.formData.brandIds[0] === String(this.brandId)) {
        return true
      }

      const { competitors } = this.$store.state.brand.settings

      const allSelectedCompetitors = competitors
        .filter(obj => includes(this.formData.brandIds, obj.id))

      return allSelectedCompetitors.every(obj => obj.enableSponsoredContent)
    }
  },

  methods: {
    async onOpenRangeDropdown (metric) {
      if (isEmpty(this.filterMetrics[metric])) {
        await this.$store.dispatch('measure/loadFilterMetrics', { metric, brandId: this.brandId })
      }
    },
    disableDayFunc (year, month, day) {
      const d1 = new Date(year, month, day)

      return d1 > new Date()
    },
    onApplyClick () {
      this.$emit('apply', this.formData)
    },
    onClearClick () {
      this.formData = defaultFormData()
      this.formData.brandIds = [ String(this.brandId) ]
      this.formData.distribution = 'all'
    },
    onMetricDaysInput (value) {
      this.formData.metricDateStart = value[0]
      this.formData.metricDateEnd = value[1]
    },
    onCreatedDaysInput (value) {
      this.formData.createdDateStart = value[0]
      this.formData.createdDateEnd = value[1]
    },
    initFilters () {
      return new Promise((resolve, reject) => {
        try {
          this.formData = Object.assign({}, this.formData, this.filters)
          resolve(true)
        } catch (err) {
          reject(err)
        }
      })
    }
  },

  watch: {
    visible (newValue) {
      if (newValue) { return this.initFilters() }
    }
  }
}
</script>

<style scoped lang="scss">
@import '../Nebula/style.scss';

.measureMoreFilters {
  position: absolute;
  background-color: #fff;
  z-index: 6;
  width: 1185px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);

  &-titleBar {
    border-bottom: 1px solid #ccc;
    display: flex;
    padding: 1.5em;
    justify-content: space-between;

    &-title {
      h3 {
        font-size: 16px;
        color: $slate-grey;
        font-stretch: normal;
        font-weight: 700;
        font-style: normal;
        line-height: normal;
        letter-spacing: normal;
        margin: 0;
      }
    }

    &-controls {
      display: flex;

      &-clear {
        padding-right: 1.5em;
        margin-right: 1.5em;
        border-right: 2px solid #e6e6e6;
        display: flex;
        align-items: center;
      }

      &-buttons {
        display: flex;
      }

      &-count {
        border-radius: 50%;
        width: 20px;
        height: 20px;
        background-color: $tiffany-blue;
        box-sizing: border-box;
        display: flex;
        justify-content: center;
        align-items: center;
        margin-left: 5px;

        span {
          display: block;
          color: white;
          font-size: 12px;
          font-weight: 500;
          letter-spacing: -0.7px;
          line-height: 1;
          font-family: $defaultFont;
        }
      }
    }
  }

  &-content {
    &-toggles {
      display: flex;
      align-items: center;
      padding: 1.5em;
    }

    &-filterSection {
      display: flex;
      flex-direction: column;
      padding: 0 1.5em 1.5em 1.5em;

      &-title {
        border-bottom: 1px solid #ccc;
        padding: 0.5em 0;

        h3 {
          font-size: 16px;
          font-weight: 700;
          font-stretch: normal;
          font-style: normal;
          line-height: normal;
          letter-spacing: normal;
          color: $slate-grey;
          margin: 0;
        }
      }

      &-filters {
        display: flex;
        align-items: center;
        padding: 1.5em 0;
      }
    }
  }
}
</style>

<style lang="scss">
.measureMoreFilters {
  .nebFormControl {
    margin-bottom: 0 !important;
    padding-right: 2em;

    .nebFormControl-content-wrapper {
      span:not(.el-checkbox-button__inner) {
        width: 100% !important;
      }
    }
  }
}
</style>
